import {
  CheckCircle, Clock, CornerLeftDown, Edit, Eye, Lock, Percent,
  RotateCcw, Tag,
  XCircle,
} from 'react-feather';
import { DocumentNode } from 'graphql';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { GetOrderQuery as GetOrder, Permission, RegistrationStatus } from '__generated__/graphql';
import { encodeUrl, replaceUrlParams } from '../../../../common/helpers';
import { useSession } from '../../../Auth/SessionProvider';
import GetOrderQuery from './GetOrderQuery';
import GetParticipantOrdersQuery from './GetParticipantOrdersQuery';
import OptionsDropdown, { LinkOption, Option } from '../../../../common/Common/OptionsDropdown';
import SC from '../../../../common/UI/SC';
import SellableLabel from '../../../../common/Common/SellableLabel';
import StatusLabels from './StatusLabels';
import UI from '../../../../common/UI';
import useApplyCreditModal from './useApplyCreditModal';
import useEditPurchaseModal from './useEditPurchaseModal';
import useLocale from '../../../../common/useLocale';
import useMediaDevice from '../../../../common/useMediaDevice';
import useProject from '../../../useProject';
import useRefundOrderModal from './useRefundOrderModal';
import useSetStatusModal from '../About/useSetStatusModal';
import useTimeSlotFormatter from '../../../../common/Tickets/useTimeSlotFormatter';
import useTogglePurchaseMutations from '../About/useTogglePurchaseMutations';

type Order = GetOrder['order'];
type Purchase = GetOrder['order']['purchases'][0];

interface ContainerProps {
  tableColumns: string;
}

export const Container = styled(UI.Div)<SC<ContainerProps>>`
  ${({ theme, sc: { tableColumns } = {} }) => css`
    @media ${theme.devices.sm} {
      display: flex;
      flex-wrap: wrap;
      padding: ${theme.gutter / 2}px 0;
    }

    @media ${theme.devices.md} {
      display: grid;
      grid-template-columns: ${tableColumns};
      padding: 0;
    }
  `}
`;

export const Cell = styled(UI.Div)`
  ${({ theme }) => css`
    padding: ${theme.gutter / 2}px ${theme.gutter / 4}px;

    &:first-child {
      padding-left: ${theme.gutter / 2}px;
    }

    &:last-child {
      padding-right: ${theme.gutter / 2}px;
    }
  `}
`;

export interface PurchaseDetailsProps {
  purchase: Purchase;
  order: Order;
  tableColumns?: string;
  refetchQueries?: DocumentNode[];
}

const PurchaseDetails = ({
  purchase,
  order,
  tableColumns,
  refetchQueries = [GetParticipantOrdersQuery, GetOrderQuery],
}: PurchaseDetailsProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();
  const { hasPermission } = useSession();
  const project = useProject();
  const device = useMediaDevice();
  const formatTimeSlot = useTimeSlotFormatter();

  const hasEditPermission = hasPermission(project.id, [Permission.edit_registrations]);
  const hasRefundPermission = hasPermission(project.id, [Permission.refunds]);
  const hasOptions = hasEditPermission || hasRefundPermission || purchase.ticket;

  const [openEditPurchaseModal] = useEditPurchaseModal({
    purchaseId: purchase.id,
    eventId: order.event.id,
    refetchQueries,
  });

  const [openRefundModal] = useRefundOrderModal({
    orderId: order.id,
    purchaseId: purchase.id,
  });

  const [openApplyCreditModal] = useApplyCreditModal({
    purchaseId: purchase.id,
    eventId: order.event.id,
    refetchQueries,
  });

  const [openSetStatusModal] = useSetStatusModal({
    registrationId: purchase.registration?.id,
    refetchQueries,
  });

  const { activatePurchase, deactivatePurchase } = useTogglePurchaseMutations({
    refetchQueries,
  });

  const canRefund = purchase.refundable_amount > 0;
  const canApplyCredit = order.participant.balance > 0 && purchase.amount > purchase.revenue;
  const isCancelled = purchase.ticket && purchase.registration
    ? purchase.registration.status === RegistrationStatus.cancelled : !purchase.active;
  const isWaitlist = purchase.registration?.status === RegistrationStatus.waitlist;
  const isPreregistration = purchase.registration?.status === RegistrationStatus.preregistration;

  const muted = !purchase.active;

  const isSeparateTicketUpgrade = order.purchases
    .filter((orderPurchase) => orderPurchase.ticket && orderPurchase.registration?.id === purchase.registration?.id)
    .length === 0;

  const isStandAloneUpgrade = purchase.registration === null;

  return (
    <Container
      sc={{ tableColumns, background: purchase.product ? 'gray.50' : undefined }}
      data-testid="Purchase"
    >
      <Cell sc={{ flexSelf: '75%', muted }}>
        <UI.FlexContainer>
          {purchase.ticket && (
            <UI.Icon sc={{ pt: 0.6, mr: 2 }}>
              <UI.Icons.Ticket />
            </UI.Icon>
          )}
          {purchase.product && !isSeparateTicketUpgrade && !isStandAloneUpgrade && (
            <UI.Div sc={{ mr: 2 }} style={{ width: 16 }} />
          )}
          <UI.Div>
            {purchase.ticket && (
              <UI.Span sc={{ deleted: isCancelled }}>
                <UI.Strong sc={{ color: 'gray.800' }}>
                  {purchase.ticket.title}
                </UI.Strong>
                {purchase.batch_size > 1 && ` (${purchase.unit_id}/${purchase.batch_size})`}
              </UI.Span>
            )}
            {purchase.product && (
              <>
                <UI.Span sc={{ deleted: isCancelled, color: 'gray.800', mr: 1 }}>
                  {purchase.product.title}
                </UI.Span>
                {purchase.product_variant && (
                  <SellableLabel type="product">
                    {purchase.product_variant.title}
                  </SellableLabel>
                )}
              </>
            )}
            <UI.FlexContainer sc={{ muted: true, small: true, flex: { wrap: 'wrap' } }}>
              {purchase.promotion.title && (
                <UI.Span sc={{ mr: 2 }}>
                  <UI.Icon sc={{ mr: 1 }}>
                    <Tag />
                  </UI.Icon>
                  {purchase.promotion.title}
                </UI.Span>
              )}
              {purchase.time_slot && (
                <UI.Span sc={{ mr: 2 }}>
                  <UI.Icon sc={{ mr: 1 }}>
                    <Clock />
                  </UI.Icon>
                  <UI.Delimit>
                    {formatTimeSlot(purchase.time_slot)}
                    {purchase.time_slot.title}
                  </UI.Delimit>
                </UI.Span>
              )}
              {purchase.coupon_code && (
                <UI.Span sc={{ mr: 2 }}>
                  <UI.Icon sc={{ mr: 1 }}>
                    <Percent />
                  </UI.Icon>
                  {purchase.coupon_code.value}
                </UI.Span>
              )}
              {purchase.invitation_code && (
                <UI.Span>
                  <UI.Icon sc={{ mr: 1 }}>
                    <Lock />
                  </UI.Icon>
                  {purchase.invitation_code.value}
                </UI.Span>
              )}
            </UI.FlexContainer>
          </UI.Div>
        </UI.FlexContainer>
      </Cell>

      {!device.isSmall && (
        <>
          <Cell sc={{ muted: true, small: true }}>
            <UI.Div sc={{ pt: 0.5 }}>
              {(purchase.ticket || isSeparateTicketUpgrade) && purchase.registration && (
                `ATL${purchase.registration.sequence}`
              )}
            </UI.Div>
          </Cell>
          <Cell>
            {(purchase.ticket || isSeparateTicketUpgrade)
              && (!purchase.registration || purchase.registration.assigned) && (
              <UI.Link
                to={`/${project.id}/participants/${purchase.participant.id}${replaceUrlParams({
                  event: order.event.id,
                  search: purchase.registration ? `ATL${purchase.registration.sequence}` : undefined,
                })}`}
              >
                {purchase.registration?.full_name || purchase.participant.full_name}
              </UI.Link>
            )}
            {(purchase.ticket || isSeparateTicketUpgrade)
              && purchase.registration && !purchase.registration.assigned && (
              <UI.Span sc={{ muted: true }}>
                {t('unassigned')}
              </UI.Span>
            )}
          </Cell>
        </>
      )}

      <Cell
        sc={{
          flexSelf: '25%',
          flex: device.isSmall && { direction: 'column', alignItems: 'flex-end' },
          muted: purchase.amount === 0,
        }}
      >
        {formatCurrency(purchase.amount)}
      </Cell>

      {device.isSmall && purchase.ticket && (
        <UI.FlexContainer sc={{ flexSelf: '100%', justifyContent: 'space-between' }}>
          <Cell sc={{ muted: true }}>
            {`ATL${purchase.registration.sequence}`}
          </Cell>
          <Cell>
            {purchase.registration.assigned && (
              <UI.Link
                to={`/${project.id}/participants/${purchase.participant.id}${replaceUrlParams({
                  event: order.event.id,
                  search: `ATL${purchase.registration.sequence}`,
                })}`}
              >
                {purchase.registration.full_name}
              </UI.Link>
            )}
            {!purchase.registration.assigned && (
              <UI.Span sc={{ muted: true }}>
                {t('unassigned')}
              </UI.Span>
            )}
          </Cell>
        </UI.FlexContainer>
      )}

      <Cell sc={{ textAlign: 'right' }}>
        {((purchase.amount + purchase.remaining_amount + purchase.refunded_amount + purchase.balance > 0)
          || isWaitlist || isPreregistration) && (
          // Only show labels if there is something to show
          <StatusLabels
            amount={purchase.amount}
            remainingAmount={purchase.remaining_amount}
            refundedAmount={purchase.refunded_amount}
            balance={purchase.balance}
            cancelled={!purchase.active}
            waitlist={isWaitlist}
            preregistration={isPreregistration}
            draft={order.draft}
            sc={{ justifyContent: [undefined, 'flex-end'] }}
          />
        )}
      </Cell>

      <Cell sc={{ py: 1.25 }} style={{ marginLeft: 'auto' }}>
        {hasOptions && (
          <OptionsDropdown>
            {hasEditPermission && purchase.ticket && (
              <Option
                icon={<CheckCircle />}
                label={t('set_status')}
                onClick={() => openSetStatusModal()}
              />
            )}
            {hasEditPermission && !purchase.ticket && purchase.active && (
              <Option
                icon={<XCircle />}
                label={t('cancel_purchase')}
                onClick={() => deactivatePurchase(purchase)}
              />
            )}
            {hasEditPermission && !purchase.ticket && !purchase.active && purchase.participant.active && (
              <Option
                icon={<CheckCircle />}
                label={t('reactivate_purchase')}
                onClick={() => activatePurchase(purchase)}
              />
            )}
            {hasEditPermission && (
              <Option
                icon={<Edit />}
                label={t('edit_purchase')}
                onClick={() => openEditPurchaseModal()}
              />
            )}
            {hasRefundPermission && canRefund && (
              <Option
                icon={<RotateCcw />}
                label={t('refund_purchase')}
                onClick={() => openRefundModal()}
              />
            )}
            {hasEditPermission && canApplyCredit && (
              <Option
                icon={<CornerLeftDown />}
                label={t('apply_credit')}
                onClick={() => openApplyCreditModal()}
              />
            )}
            {purchase.ticket && (
              <LinkOption
                icon={<Eye />}
                label={t('view_registration')}
                to={`/${project.id}/participants/${order.participant.id}?${encodeUrl({ event: order.event.id, search: `ATL${purchase.registration.sequence}` })}`}
              />
            )}
          </OptionsDropdown>
        )}
      </Cell>
    </Container>
  );
};

export default PurchaseDetails;
